import React, {createContext, ReactNode, useContext, useEffect, useState} from 'react';
import axios from "axios";
import {accessTokenKey, authRedirectReason, refreshTokenKey, usernameKey} from "./AuthInterceptor";
import {useOnMount} from "../hooks/UseOnMount";
import {useSnackbar} from "../contexts/SnackbarContext";
import SessionExpiredDialog from "../components/auth/SessionExpiredDialog";
import {deleteCookie, getCookie, setCookie} from "../utils/cookies";
import {useTranslation} from "react-i18next";

export interface UserDetails {
    id: number;
    email: string;
    firstName: string | null;
    lastName: string | null;
    role: string;
    group: Array<{ id: number; name: string; createdAt: string }>;
}

interface AuthContextType {
    isAuthenticated: boolean;
    isAdmin: boolean;
    isVerifying: boolean;
    currentUserDetails: UserDetails | null;
    login: (email: string, password: string) => Promise<void>;
    logout: (isManual?: boolean) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({children}) => {
    const {showSnackbar} = useSnackbar();
    const {t} = useTranslation();
    const [isAuthenticated, setIsAuthenticated] = useState(() => {
        return !!getCookie(accessTokenKey);
    });
    const [isVerifying, setIsVerifying] = useState(true);
    const [isAdmin, setIsAdmin] = useState(false);
    const [currentUserDetails, setCurrentUserDetails] = useState<UserDetails | null>(null);
    const [sessionExpiredOpen, setSessionExpiredOpen] = useState(false);
    const isLoginRoute = typeof window !== 'undefined' && window.location.pathname === '/login';

    useOnMount(async () => {
        if (!getCookie(accessTokenKey)) {
            setIsVerifying(false);
            return;
        }
        try {
            const response = await axios.get(`/users/currentUserDetails`);
            if (response.status >= 200 && response.status <= 300) {
                const userData = response.data;
                setIsAuthenticated(true);
                setIsAdmin(userData.role === 'SYSTEM_ADMIN');
                setCurrentUserDetails(userData);
                setIsVerifying(false);
            }
        } catch {
            deleteCookie(accessTokenKey);
            deleteCookie(refreshTokenKey);
            deleteCookie(usernameKey);
            setCurrentUserDetails(null);
            setIsAdmin(false);
            setIsAuthenticated(false);
        } finally {
            setIsVerifying(false);
        }
    })

    useEffect(() => {
        const handler = () => {
            if (!sessionExpiredOpen) {
                setSessionExpiredOpen(true);
            }
        };
        window.addEventListener('session-expired', handler as any);
        return () => window.removeEventListener('session-expired', handler as any);
    }, [sessionExpiredOpen]);

    const handleSessionExpired = () => {
        setSessionExpiredOpen(false);
        logout(false);
    };

    const login = async (email: string, password: string) => {
        const response = await axios.post(`/auth/login`, {
            email,
            password,
        });

        if (response.status === 200) {
            const {accessToken, refreshToken, username} = response.data;
            setCookie(accessTokenKey, accessToken, 30);
            setCookie(refreshTokenKey, refreshToken, 30);
            setCookie(usernameKey, username, 30);
            localStorage.removeItem(authRedirectReason);
            setIsAuthenticated(true);
            setSessionExpiredOpen(false);
            try {
                const userDetailsResponse = await axios.get(`/users/currentUserDetails`);
                if (userDetailsResponse.status === 200) {
                    const userData = userDetailsResponse.data;
                    setCurrentUserDetails(userData);
                    setIsAdmin(userData.role === 'SYSTEM_ADMIN');
                }
            } catch (e) {
                setCurrentUserDetails(null);
                setIsAdmin(false);
            }
            showSnackbar('Zalogowano pomyślnie', 'success');
        }
    };

    const logout = (isManual = true) => {
        deleteCookie(accessTokenKey);
        deleteCookie(refreshTokenKey);
        deleteCookie(usernameKey);
        localStorage.removeItem(authRedirectReason);
        setSessionExpiredOpen(false);
        setIsAuthenticated(false);
        setCurrentUserDetails(null);
        setIsAdmin(false);
        window.location.href = '/login';

        if (isManual) {
            showSnackbar(t('auth.logoutSuccess'), 'success');
        } else {
            showSnackbar(t('auth.sessionExpired'), 'info');
        }
    };

    return (
        <AuthContext.Provider value={{isAuthenticated, login, logout, isVerifying, isAdmin, currentUserDetails}}>
            {children}
            <SessionExpiredDialog
                open={sessionExpiredOpen && !isLoginRoute}
                onClose={handleSessionExpired}
            />
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
